import _ from 'lodash';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import qs from 'qs';
import React, {
  useEffect,
  useMemo,
} from 'react';
import { useQuery } from '@apollo/client';
import { useCompanyMembers, usePaginatedQuery } from '@playbookux/playbook-web-core';

import { GET_TEST_SESSION_NOTE } from '../../../core/graphql/testSessionNotes';
import withProjectSidebar from '../../components/with-project-sidebar';
import Tabs from '../../constants/tabs';

import VideoFeatures from './video-features';

import {
  GET_TESTER_BY_ID,
  GET_RECORDING_BY_TEST_SESSION_ID,
  GET_PROJECT_PARTICIPANTS,
  OFFSET,
  ORDER,
  POOL_INTERVAL,
  QUERY_LIMIT,
} from './graphql';
import { config } from '../../../config';

const DEMO_PROJECT_ID = '00000000-0000-0000-0000-000000000000';

const ParticipantVideo = (props) => {
  const {
    project,
    location,
    match,
  } = props;
  const { params } = match || {};
  const { projectId, testerId } = params || {};

  const { data: companyMembers } = useCompanyMembers(project.company.id);

  const { search } = location;

  const demoTester = useMemo(() => {
    if (projectId !== DEMO_PROJECT_ID) {
      return undefined;
    }
    const { testers } = project;
    return _.find(testers, (item) => item.id === testerId);
  }, [project, projectId, testerId]);

  const { data, loading: testerLoading } = useQuery(GET_TESTER_BY_ID, {
    variables: { id: testerId },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const {
    data: participantsData,
    loading: participantsLoading,
    stopPolling,
  } = usePaginatedQuery(GET_PROJECT_PARTICIPANTS, {
    variables: {
      projectId,
      offset: OFFSET,
      limit: QUERY_LIMIT,
      tsIsApproved: true,
      removed: false,
      order: ORDER,
    },
    pollInterval: POOL_INTERVAL,
  }, {
    dataField: 'projectParticipants',
  });

  const { projectParticipants = [] } = participantsData;

  useEffect(() => {
    if (!_.isEmpty(projectParticipants) && participantsLoading) {
      stopPolling();
    }
  }, [stopPolling, projectParticipants, participantsLoading]);

  const tester = useMemo(() => (data && data.tester) || demoTester, [data, demoTester]);

  const searchQuery = qs.parse(search, { ignoreQueryPrefix: true });
  // we need to transform into string and replace . to empty, example: 1.5 to 15 and then transform to int
  const startTime = searchQuery?.time ? parseInt(String(searchQuery.time).replace(/\./g, ''), 10) : undefined;

  // if url has note on query string get and set the note to edit clip
  const skip = !searchQuery?.note;
  const { data: noteData } = useQuery(GET_TEST_SESSION_NOTE, {
    variables: { id: !skip && searchQuery?.note },
    skip,
  });

  //  We need to validate if the project is a practice project to get the right test session
  //  If it is a practice project, we need to get the approved/submitted test session if it exists
  //  If it is not a practice project, we need to get the first test session
  const recordingTestSession = useMemo(() => {
    const practiceTestProjectId = config.REACT_APP_PRACTICE_TEST_PROJECT_ID;
    const firstTestSessionId = tester?.testSessions[0].id;

    if (projectId === practiceTestProjectId) {
      const sessionApproved = _.find(tester?.testSessions, (testSession) => testSession.practiceTestStatus === 'approved');

      if (sessionApproved) return sessionApproved.id;

      const sessionSubmitted = _.find(tester?.testSessions, (testSession) => testSession.practiceTestStatus === 'submitted');

      if (sessionSubmitted) return sessionSubmitted.id;

      return firstTestSessionId;
    }

    return firstTestSessionId;
  }, [projectId, tester]);

  const { data: recordingsData, loading: recordingsLoading } = useQuery(GET_RECORDING_BY_TEST_SESSION_ID, {
    variables: { testSessionId: recordingTestSession },
    skip: !tester,
  });

  const { recordings } = recordingsData || [];
  const newSelectedNote = !_.isEmpty(noteData) ? noteData.testSessionNote : null;
  return (
    <VideoFeatures
      project={project}
      startTime={startTime}
      participants={projectParticipants || []}
      tester={tester}
      isEditClip={!skip}
      companyMembers={companyMembers}
      newSelectedNote={newSelectedNote}
      testerLoading={testerLoading}
      participantsLoading={participantsLoading}
      recordingsLoading={recordingsLoading}
      recordings={recordings || []}
    />
  );
};

ParticipantVideo.propTypes = {
  match: PropTypes.shape({
    params: {
      projectId: PropTypes.string,
      testerId: PropTypes.string,
    },
  }).isRequired,
  project: PropTypes.oneOfType([PropTypes.object]).isRequired,
  location: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

export default compose(
  withRouter,
  withProjectSidebar({
    selectedTab: Tabs.PROJECT,
    showSideBar: false,
  }),
)(ParticipantVideo);
